import React, { useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import AcceptInviteModal from './accept-invite-modal';
import { DeactivateMarketplaceModal } from './deactivate-marketplace-modal';
import commonStyles from '../settings-page.module.less';
import MarketplaceConnectionModal from './marketplace-connection-modal/settings';
import MarketplaceSetupModal from './marketplace-connection-modal/setup';
import { CONNECTED_MARKETPLACES_GUIDS } from './marketplace-connection-modal/store-connection-success-notifications';
import messages from './messages';
import SelectMarketplaceModal from './select-marketplace-modal';
import StoreRow from './store-row/store-row';
import { useTrackMarketplaceConnection, sources as MarketplaceConnectionSources, stepNames } from './tracking-events';
import { MarketplaceIds } from '../../../enums/marketplace-ids';
import Global from '../../../store/global-selectors';
import { getReferenceData } from '../../../store/reference-data/reference-data.actions';
import { getStoreErrorLogData, refreshPackingSlipTemplates, refreshStoreList } from '../../../store/seller/seller.actions';
import { WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { useModal } from '../../../hooks/use-modal';
import V2MarketplaceSettingsModal from './v2-modals/marketplace-settings';
import V2MarketplaceSetupModal from './v2-modals/marketplace-setup';
import { useCUIContext } from '../../../contexts/cui-context';
import { LocalStorageService } from '../../../services/local-storage-service';
import { linkIDs } from '../../../services/link-service/links';
import HelpArticleLink from '../../../components/help-article-link';
import { useOrderImportCustomStoreFeatureGate } from '../../../feature-gates/use-order-import-custom-store-feature-gate';
import {
  useExternalStoresAccessFeatureGate,
  useHasSingleOrderSourceForFreePlan,
} from '../../../feature-gates/use-external-stores-access-feature-gate';
import { FeatureGatedPromotionalBanner } from '../../../feature-gates/feature-gated-promotional-banner';
import { StoreErrorLogsModalV2Component } from '../../../components/modals/store-error-logs-modal-v2';
import style from './style.module.less';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

export const StoreSetup = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const trackMarketplaceConnection = useTrackMarketplaceConnection();
  const [marketplaceSelectorOpen, setMarketplaceSelectorOpen] = useState(false);
  const [setupMarketplace, setSetupMarketplace] = useState(undefined);
  const [editExistingStore, setEditExistingStore] = useState(undefined);
  const [showInactiveStores, setShowInactiveStores] = useState(false);
  const [stores, setStores] = useState([]);
  const [showCUIModal, setShowCUIModal] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStoreName, setSelectedStoreName] = useState(null);
  const { close, isOpen: deactivateMarketplaceModalIsOpen, open } = useModal();

  const [acceptInviteModalOpen, setAcceptInviteModalOpen] = useState(false);
  const allStores = useSelector(Global.selectSellerStores);

  const { isFeatureEnabled: isExternalStoresAccessFeatureEnabled } = useExternalStoresAccessFeatureGate();
  const { isFeatureEnabled: isCustomStoreFeatureEnabled } = useOrderImportCustomStoreFeatureGate();

  const activeStores = useMemo(() => allStores.filter((store) => store.isActive), [allStores]);
  const hasCustomStore = useMemo(() => allStores.some((store) => store.marketplaceId === MarketplaceIds.CustomStore), [allStores]);
  const hasExternalStore = useMemo(
    () =>
      allStores.some((store) => store.marketplaceId !== MarketplaceIds.ShipStation && store.marketplaceId !== MarketplaceIds.CustomStore),
    [allStores],
  );

  const hasSingleOrderSourceForFreePlan = useHasSingleOrderSourceForFreePlan();
  const showFeatureErrorBanner =
    (!isCustomStoreFeatureEnabled && hasCustomStore) || (!isExternalStoresAccessFeatureEnabled && hasExternalStore);

  const activeSellerExternalStores = useMemo(
    () => activeStores.filter((store) => store.marketplaceId !== MarketplaceIds.ShipStation),
    [activeStores],
  );
  const activeSellerExternalStoresWithoutCustomStore = useMemo(
    () => activeSellerExternalStores.filter((store) => store.marketplaceId !== MarketplaceIds.CustomStore),
    [activeSellerExternalStores],
  );
  const activeSellerExternalStoreGuids = useMemo(
    () => activeSellerExternalStores.map((store) => store.storeGuid),
    [activeSellerExternalStores],
  );

  const firstActiveStoreGuid = activeSellerExternalStoresWithoutCustomStore?.[0]?.storeGuid;
  const showExternalStoresAccessFeatureUpsellBanner = !isExternalStoresAccessFeatureEnabled;
  const showAddAdditonalExternalStoresUpsellBanner = hasSingleOrderSourceForFreePlan;

  // prefer to show the external stores access feature upsell banner and additonal stores banner over the custom store feature upsell banner if they don't have access to any of them
  const showCustomStoreFeatureUpsellBanner =
    !isCustomStoreFeatureEnabled && !showExternalStoresAccessFeatureUpsellBanner && !showAddAdditonalExternalStoresUpsellBanner;

  const marketplacesReference = useSelector(Global.selectReferenceDatagetMarketplaces);

  const { unsupportedMarketplaces } = useCUIContext();

  useEffect(() => {
    if (showInactiveStores) {
      setStores(allStores);
      return;
    }
    setStores(activeStores);
  }, [allStores, activeStores, showInactiveStores, marketplacesReference, intl]);

  useEffect(() => {
    if (window.location.pathname.includes('/accept')) {
      setAcceptInviteModalOpen(true);
    }
  }, []);

  useEffect(() => {
    if (editExistingStore) {
      const marketplaceIsSupported = !unsupportedMarketplaces.has(editExistingStore?.marketplaceId);
      setShowCUIModal(marketplaceIsSupported);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [editExistingStore]);

  const toggleActiveStores = () => setShowInactiveStores((val) => !val);
  const onClickAddStore = useCallback(() => {
    setMarketplaceSelectorOpen(true);
    LocalStorageService.setItem(CONNECTED_MARKETPLACES_GUIDS, activeSellerExternalStoreGuids);
    trackMarketplaceConnection(WorkflowSteps.START, {
      source: MarketplaceConnectionSources.SETTINGS_PAGE,
    });
  }, [activeSellerExternalStoreGuids, trackMarketplaceConnection]);

  const onMarketplaceSelected = useCallback(
    (marketplace) => {
      setSetupMarketplace(marketplace);
      setMarketplaceSelectorOpen(false);
      trackMarketplaceConnection(WorkflowSteps.CLICK, {
        selectedMarketplace: marketplace,
        marketplaceName: marketplace?.name,
        marketplaceId: marketplace?.marketplaceId,
      });
    },
    [trackMarketplaceConnection],
  );

  const onRedirectToMarketplaceSetup = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.REDIRECT, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: setupMarketplace?.name,
      marketplaceId: setupMarketplace?.marketplaceId,
    });
  }, [setupMarketplace, trackMarketplaceConnection]);

  const onRedirectToMarketplaceEditing = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.REDIRECT);
  }, [trackMarketplaceConnection]);

  const onEditExistingMarketplace = useCallback(
    (marketplace) => {
      setEditExistingStore(marketplace);
      trackMarketplaceConnection(WorkflowSteps.START, {
        selectedMarketplace: marketplace,
        marketplaceName: marketplace?.name,
        marketplaceId: marketplace?.marketplaceId,
        editExistingStore: true,
        source: MarketplaceConnectionSources.SETTINGS_PAGE,
      });
    },
    [trackMarketplaceConnection],
  );

  const onCloseEditConnectionModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      stepName: stepNames.STORE_SPECIFIC_SETUP_MODAL,
    });
    setEditExistingStore(undefined);
  }, [trackMarketplaceConnection]);

  const onSaveEditConnectionModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.COMPLETE);
    setEditExistingStore(undefined);
  }, [trackMarketplaceConnection]);

  const onCloseSelectMarketplaceModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      stepName: stepNames.SELECT_MARKETPLACE_MODAL,
    });
    setMarketplaceSelectorOpen(false);
  }, [trackMarketplaceConnection]);

  const onCloseSetupModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: setupMarketplace?.name,
      marketplaceId: setupMarketplace?.marketplaceId,
      stepName: stepNames.STORE_SPECIFIC_SETUP_MODAL,
    });
    setSetupMarketplace(undefined);
  }, [setupMarketplace, trackMarketplaceConnection]);

  const onCloseAcceptInviteModal = useCallback(() => {
    setAcceptInviteModalOpen(false);
  }, []);

  const openDeactivateMarketplaceModal = useCallback(
    (storeName, storeId) => {
      setSelectedStoreId(storeId);
      setSelectedStoreName(storeName);
      open();
    },
    [open],
  );

  const onSaveNewMarketplace = useCallback(() => {
    const { marketplaceId, name } = setupMarketplace;
    if (!marketplacesReference.some((market) => market.marketplaceId === marketplaceId)) {
      dispatch(getReferenceData());
    }
    dispatch(refreshStoreList());
    dispatch(refreshPackingSlipTemplates());
    trackMarketplaceConnection(WorkflowSteps.COMPLETE, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: name,
      marketplaceId: setupMarketplace?.marketplaceId,
    });
    LocalStorageService.clearItemsWithPrefix(CONNECTED_MARKETPLACES_GUIDS);
    setSetupMarketplace(undefined);
  }, [dispatch, marketplacesReference, setupMarketplace, trackMarketplaceConnection]);

  const onMarketplaceSetupBack = useCallback(() => {
    setSetupMarketplace(undefined);
    setMarketplaceSelectorOpen(true);
  }, [setMarketplaceSelectorOpen, setSetupMarketplace]);

  const onSetupError = useCallback(
    (error) => {
      const errorMessage = error?.error?.ExceptionMessage || error?.message;
      trackMarketplaceConnection(WorkflowSteps.ERROR, {
        error,
        errorMessage,
      });
    },
    [trackMarketplaceConnection],
  );

  const [displayStoreErrorModal, setDisplayStoreErrorModal] = useState(false);
  const [currentStoreGuid, setCurrentStoreGuid] = useState(null);
  const storeErrorLogs = useSelector(Global.selectSellerStoreErrorLogs);
  const handleErrorModalVisibility = useCallback(
    (storeGuid) => {
      if (displayStoreErrorModal) {
        setDisplayStoreErrorModal(false);
        setCurrentStoreGuid(null);
        return;
      }
      setDisplayStoreErrorModal(true);
      setCurrentStoreGuid(storeGuid);
      dispatch(getStoreErrorLogData(storeGuid));
    },
    [dispatch, displayStoreErrorModal, setDisplayStoreErrorModal],
  );

  return (
    <div className={commonStyles['content-wrapper']}>
      {displayStoreErrorModal ? (
        <StoreErrorLogsModalV2Component
          onModalClose={handleErrorModalVisibility}
          storeErrorLogs={storeErrorLogs}
          storeGuid={currentStoreGuid}
        />
      ) : null}
      <div className={classnames(commonStyles['limited-width-page'], commonStyles.content, style['store-list-page'])}>
        <SettingsPageTitle value={messages.storeSetupPageTitle} />

        <div className={classnames(style.storeSetupContentContainer)}>
          <div className={style.storeSetupContent}>
            <div className={style.subtext}>
              <FormattedMessage
                {...messages.storeSetupPageSubtext}
                values={{
                  a: (chunks) => <HelpArticleLink articleId={linkIDs.EditStoreSettings}>{chunks}</HelpArticleLink>,
                }}
              />
            </div>
            {showAddAdditonalExternalStoresUpsellBanner ? (
              <FeatureGatedPromotionalBanner featureName={<FormattedMessage {...messages.additionalStoresFeatureName} />} />
            ) : null}
            {showExternalStoresAccessFeatureUpsellBanner ? (
              <FeatureGatedPromotionalBanner featureName={<FormattedMessage {...messages.externalStoresAccessFeatureName} />} />
            ) : null}
            {showCustomStoreFeatureUpsellBanner ? (
              <FeatureGatedPromotionalBanner featureName={<FormattedMessage {...messages.customsStoreFeatureName} />} />
            ) : null}
            {showFeatureErrorBanner ? (
              <div>
                <BannerMessageV2
                  content={<Text textType={TextType.Caption} value={messages.storeFeatureGatedMessage} />}
                  header={<Text textType={TextType.label} value={messages.storeFeatureGatedTitle} />}
                  layout={BannerLayout.Full}
                  status={BannerStatus.Error}
                />
              </div>
            ) : null}
            <div className={classnames(style['add-store-container'])}>
              <Checkbox
                checked={showInactiveStores}
                className={style.checkbox}
                label={<FormattedMessage {...messages.showInactiveStores} />}
                onChange={toggleActiveStores}
              />
              <Button onClick={onClickAddStore} theme={THEME_CONSTANTS.primary}>
                <FormattedMessage {...messages.addNewStoreButtonText} />
              </Button>
            </div>
            <table className={classnames(style['store-list-container'])}>
              <thead>
                <tr>
                  <th className={style['left-head-cell']}>
                    <FormattedMessage defaultMessage='Store Name' description='settings > Store Setup > grid > Store Name' id='MZvEZY' />
                  </th>
                  <th className={style['last-modified-header']}>
                    <FormattedMessage
                      defaultMessage='Last Modified'
                      description='settings > Store Setup > grid > Last Modified'
                      id='ZssejF'
                    />
                  </th>
                  <th className={style['status-header']}>
                    <FormattedMessage
                      defaultMessage='Store Import Status'
                      description='settings > Store Setup > grid > Store Import Status'
                      id='CWu/WD'
                    />
                  </th>
                  <th className={style['right-head-cell']}>
                    <FormattedMessage defaultMessage='Actions' description='settings > Store Setup > grid > Actions' id='tHVHaT' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store, index) => {
                  const marketplaceId = Number.parseInt(store.marketplaceId, 10);
                  const isCustom = marketplaceId === MarketplaceIds.CustomStore;
                  const isExternal = marketplaceId !== MarketplaceIds.ShipStation && marketplaceId !== MarketplaceIds.CustomStore;
                  const isMarketPlaceGated =
                    (isCustom && !isCustomStoreFeatureEnabled) ||
                    (isExternal &&
                      (hasSingleOrderSourceForFreePlan
                        ? firstActiveStoreGuid && store.storeGuid !== firstActiveStoreGuid
                        : !isExternalStoresAccessFeatureEnabled));

                  return (
                    <StoreRow
                      key={store.storeGuid + index}
                      isMarketPlaceGated={isMarketPlaceGated}
                      openDeactivateMarketplaceModal={openDeactivateMarketplaceModal}
                      openErrorLog={handleErrorModalVisibility}
                      openMarketplaceConnectionModal={onEditExistingMarketplace}
                      store={store}
                    />
                  );
                })}
              </tbody>
            </table>
            {marketplaceSelectorOpen ? (
              <SelectMarketplaceModal onClose={onCloseSelectMarketplaceModal} onMarketplaceSelected={onMarketplaceSelected} />
            ) : null}
            {setupMarketplace && !setupMarketplace.isSupported ? (
              <V2MarketplaceSetupModal
                marketplaceId={setupMarketplace.marketplaceId}
                onClose={onCloseSetupModal}
                onSave={onSaveNewMarketplace}
              />
            ) : null}
            {setupMarketplace && setupMarketplace.isSupported ? (
              <MarketplaceSetupModal
                marketplaceApiCode={setupMarketplace.apiCode}
                onBack={onMarketplaceSetupBack}
                onClose={onCloseSetupModal}
                onError={onSetupError}
                onRedirect={onRedirectToMarketplaceSetup}
                onSave={onSaveNewMarketplace}
                redirectPath='/settings/stores'
              />
            ) : null}
            {editExistingStore && !showCUIModal ? (
              <V2MarketplaceSettingsModal
                onClose={onCloseEditConnectionModal}
                onSave={onSaveEditConnectionModal}
                storeGuid={editExistingStore.storeGuid}
              />
            ) : null}
            {editExistingStore && showCUIModal ? (
              <MarketplaceConnectionModal
                onClose={onCloseEditConnectionModal}
                onRedirect={onRedirectToMarketplaceEditing}
                onSave={onSaveEditConnectionModal}
                redirectPath='/settings/stores'
                storeGuid={editExistingStore.storeGuid}
              />
            ) : null}
            {deactivateMarketplaceModalIsOpen ? (
              <DeactivateMarketplaceModal onClose={close} storeId={selectedStoreId} storeName={selectedStoreName} />
            ) : null}
            {acceptInviteModalOpen ? <AcceptInviteModal onClose={onCloseAcceptInviteModal} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};
